import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ISTabLayout, ISTabItem } from './is-tab-layout';
import { AppService } from '../services/app.service';

@Component({
  selector: 'is-tab-layout',
  templateUrl: './is-tab-layout.component.html',
  styleUrls: ['./is-tab-layout.component.scss']
})
export class ISTabLayoutComponent implements OnInit {
  @ViewChild('contentTabLayout') content!: ElementRef<HTMLDivElement>;
  @ViewChild('tabList') tabListRef!: ElementRef<HTMLUListElement>;
  @Input() tabLayout$: BehaviorSubject<ISTabLayout> = new BehaviorSubject<ISTabLayout>(new ISTabLayout(1, []));

  // * Tab Layout Styles
  /**
   * Visual style for the tab layout component.
   *
   * Options:
   * - 'default'  : default look. siga-> modern, other apps-> equivalent to 'old'
   * - 'old-dark' : Legacy dark theme.
   * - 'buttons'  : Button-like tabs.
   * - 'old'      : original theme.
   *
   * @default 'default'
   */
  @Input() style: 'default' | 'old-dark' | 'buttons' | 'old' = 'default';

  /**
   * Toggle rendering of icons inside tabs.
   *
   * When true, icons provided in ISTabItem will be displayed.
   * When false, only labels/badges are shown.
   *
   * @default true
   */
  @Input() showIcons: boolean = true;

  /**
   * Height for the tab list container — this is list max height.
   *
   * Accepts a CSS size number (treated as pixels).
   * When provided, this value is applied as the element's max-height to constrain the list's
   * visible height and enable scrolling when content overflows.
   *
   * @remarks
   * - If omitted, the list will size naturally based on its content (max-height: 100%).
   * - Passing a number is interpreted as pixels.
   *
   * @example
   * // as pixels
   * height = 200;
   *
   * @default undefined
   */
  @Input() height: any

  @Output() onSelectedTab: EventEmitter<any> = new EventEmitter<any>();

  hasOverflow = false;
  atStart = true;
  atEnd = false;

  constructor(private _cd: ChangeDetectorRef, private app: AppService) { }

  get appId(): string {
    return this.app.appId as unknown as string;
  }

  ngOnInit(): void { }

  // ========== Overflow handlers ==========
  onOverflow(val: boolean) { this.hasOverflow = val; }
  onScrollStart(val: boolean) { this.atStart = val; }
  onScrollEnd(val: boolean) { this.atEnd = val; }

  // ========== Click/select ==========
  onTabClick(selectedTabID: any) {
    const layout = this.tabLayout$.value;
    layout.selectedMenu = selectedTabID;
    this.onSelectedTab.emit(selectedTabID);
    this.scrollToCurrentTab();
  }

  // ========== Public API ==========
  /** Atualiza o badge (número) de um separador pelo id */
  public setTabBadge(menuId: any, count: number): void {
    const layout = this.tabLayout$.value;
    const idx = layout.tabItems.findIndex(t => t.id === menuId);
    if (idx === -1) { return; }
    (layout.tabItems[idx] as ISTabItem).badge = count;
    this._cd.markForCheck();
    this._cd.detectChanges();
  }

  // ========== Scrolling ==========
  private get scrollElement(): HTMLElement {
    return this.content?.nativeElement ?? ({} as any);
  }

  scrollToRight(pixels: number) {
    const element = this.scrollElement;
    element.scrollTo({
      left: element.scrollLeft + pixels,
      behavior: 'smooth'
    });
  }

  scrollToLeft(pixels: number) {
    const element = this.scrollElement;
    element.scrollTo({
      left: element.scrollLeft - pixels,
      behavior: 'smooth'
    });
  }

  scrollToTop(pixels: number) {
    const element = this.scrollElement;
    element.scrollTo({
      top: element.scrollTop - pixels,
      behavior: 'smooth'
    });
  }

  scrollToBottom(pixels: number) {
    const element = this.scrollElement;
    element.scrollTo({
      top: element.scrollTop + pixels,
      behavior: 'smooth'
    });
  }

  private scrollToCurrentTab() {
    const layout = this.tabLayout$.value;
    const currentItem = layout.tabItems.find(t => t.id === layout.selectedMenu);
    const elementId = currentItem ? (currentItem.label + currentItem.id) : undefined;
    if (!elementId) { return; }
    const el = document.getElementById(elementId);
    if (el && typeof el.scrollIntoView === 'function') {
      if (!this.tabLayout$.value.verticalLayout) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      else {
        const container = this.scrollElement;
        // Ensure we have a scrollable container and it supports smooth scrolling
        if (container && typeof container.scrollTo === 'function') {
          // Get bounding rectangles for the tab element and its scroll container
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();

          // Calculate vertical offset to center the tab element within the container.
          // offset = distance from container top to element top
          //        + current container vertical scroll
          //        - half the container height (so element ends up centered)
          //        + half element height correction (so center aligns)
          const offset = elRect.top - containerRect.top + container.scrollTop - (container.clientHeight / 2 - elRect.height / 2);

          // Smoothly scroll the container to the calculated vertical position
          container.scrollTo({ top: offset, behavior: 'smooth' });
        }
      }
    }
  }

  // Keep arrows visibility responsive when viewport changes
  @HostListener('window:resize')
  onResize() {
    // trigger overflow directive check via microtask
    setTimeout(() => this._cd.detectChanges());
  }
}
